import React, { useContext, useEffect, useState } from "react";
import "./FilterManagement.scss";
import FiltersMenu from "./FiltersMenu/FiltersMenu";
import { FormProvider, useForm } from "react-hook-form";
import ClassNames from "classnames";
import { FiltersContext } from "./FilterProvider/FilterProvider";
import SelectedFilterManagement from "./SelectedFilterManagement/SelectedFilterManagement";
import { FilterObject, FiltersToPin } from "./CommonFilterTypes";
import type { FilterEntity } from "./filterEntity";

interface FilterManagementProps {
  showFilterActions?: boolean;
  setDefaultsForScopeAndFilter?: boolean;
  hasEditMode?: boolean;
  displayAsInput?: boolean;
  readOnly?: boolean;
  enableChangeFilters?: boolean;
  enableSaveFilters?: boolean;
  disabled?: boolean;
  selectedFilter?: FilterObject | null;
  hideSaveAs?: boolean;
  isVertical?: boolean;
  onFilterSave?: (filter: FilterObject) => void;
  displayMenuHeader?: boolean;
  className?: string;
  entity?: FilterEntity;
  fieldsToIgnore?: string[];
  isInline?: boolean;
  fieldsToIgnoreWarningMsg?: string;
  useSameWidth?: boolean;
  filtersToPin?: FiltersToPin;
  viewMode?: boolean;
  fieldsToHide?: string[];
  unsupportedFilterFields?: string[];
  // Set to true in contexts that do not have an Apply button (e.g. Rules /
  // Automation Wizard edit-filter modals), where Save/Save As must remain
  // enabled even when pending unapplied changes exist.
  allowSaveWithPendingChanges?: boolean;
  locationContext?: string;
}

const FilterManagement: React.FC<FilterManagementProps> = ({
  showFilterActions,
  setDefaultsForScopeAndFilter,
  hasEditMode = false,
  displayAsInput = false,
  readOnly = false,
  enableChangeFilters = true,
  enableSaveFilters = true,
  disabled = false,
  selectedFilter = null,
  hideSaveAs,
  isVertical,
  onFilterSave = () => {},
  displayMenuHeader = false,
  className,
  entity,
  fieldsToIgnore = [],
  isInline = false,
  fieldsToIgnoreWarningMsg,
  useSameWidth,
  filtersToPin,
  viewMode = false,
  fieldsToHide = [],
  unsupportedFilterFields = [],
  allowSaveWithPendingChanges,
  locationContext,
}) => {
  const [isLoadingFiltersData, setIsLoadingFiltersData] = useState(false);
  const methods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      saveAsFilterName: "",
      filterPanels: [],
    },
  });

  const {
    filtersState: { selectedFilter: selectedFilterFromState },
    updateFiltersState,
  } = useContext(FiltersContext);

  useEffect(() => {
    if (!!selectedFilter) {
      updateFiltersState({
        selectedFilter,
        filterToExecute: selectedFilter?.value?.query,
      });
    }
  }, [selectedFilter]);

  const showFilterPanel =
    !viewMode &&
    (!displayAsInput ||
      (displayAsInput && !!selectedFilterFromState?.value?.query));

  const classNameWrapper = ClassNames(className && className, {
    "has-edit-mode": hasEditMode,
    "read-only": readOnly,
    "is-disabled": disabled,
    "input-wrap input-wrap-filter": displayAsInput,
    "is-vertical": isVertical,
    "has-panel": showFilterPanel,
  });

  return (
    <FormProvider {...methods}>
      <div className={`${classNameWrapper}`}>
        <FiltersMenu
          setDefaultsForScopeAndFilter={setDefaultsForScopeAndFilter}
          readOnly={readOnly || !enableChangeFilters}
          disabled={disabled}
          onLoadingFiltersData={setIsLoadingFiltersData}
          displayAsInput={displayAsInput}
          displayMenuHeader={displayMenuHeader}
          useSameWidth={useSameWidth}
          viewMode={viewMode}
          locationContext={locationContext}
        />
        <div className="filter-separator" />
        {showFilterPanel && (
          <SelectedFilterManagement
            showFilterActions={showFilterActions}
            hasEditMode={hasEditMode}
            setDefaultsForScopeAndFilter={setDefaultsForScopeAndFilter}
            readOnly={readOnly}
            isLoadingFiltersData={isLoadingFiltersData}
            enableSaveFilters={enableSaveFilters}
            hideSaveAs={hideSaveAs}
            onFilterSave={onFilterSave}
            entity={entity}
            fieldsToIgnore={fieldsToIgnore}
            isInline={isInline}
            fieldsToIgnoreWarningMsg={fieldsToIgnoreWarningMsg}
            filtersToPin={filtersToPin}
            fieldsToHide={fieldsToHide}
            unsupportedFilterFields={unsupportedFilterFields}
            allowSaveWithPendingChanges={allowSaveWithPendingChanges}
          />
        )}
      </div>
    </FormProvider>
  );
};

export default FilterManagement;
